import { useEffect, useMemo, useState } from "react";
import { Link } from "wouter";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { ChevronDown, ChevronRight, CreditCard, FileSignature, FileText, Mail, MessageSquare, Receipt, Wrench, type LucideIcon } from "lucide-react";
import { DELIVERY_LABEL, paths, type Agreement, type DocSend, type Estimate, type Invoice, type Job, type MessageLog, type PortalContact } from "@clevis/shared-types";
import { Card, CardTitle, Select, cn } from "@clevis/shared-ui";
import { db } from "../lib/firebase";

// Customer → History: everything that happened with this customer, newest
// first, in one list — texts in and out, estimates and invoices (sent,
// emailed, opened, signed, accepted), payments, jobs booked and finished,
// documents sent, signed agreements, portal sign-ins and POs sent in.
// Built from the records themselves, so it's never out of step.

type Kind = "text" | "email" | "money" | "job" | "doc";
interface Ev {
  at: number;
  kind: Kind;
  icon: LucideIcon;
  title: string;
  detail?: string;
  href?: string;
  tone?: "good" | "bad";
}

const money = (c: number) => `$${(c / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const when = (ms: number) => {
  const d = new Date(ms);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return `${d.toLocaleDateString(undefined, { month: "short", day: "numeric", ...(sameYear ? {} : { year: "numeric" }) })}, ${d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`;
};
const FILTERS: Array<{ id: "all" | Kind; label: string }> = [
  { id: "all", label: "Everything" },
  { id: "text", label: "Texts" },
  { id: "email", label: "Emails" },
  { id: "money", label: "Estimates, invoices and payments" },
  { id: "job", label: "Jobs" },
  { id: "doc", label: "Documents and portal" },
];

function useList<T>(tenantId: string, col: string, field: string, value: string): Array<T & { id: string }> {
  const [rows, setRows] = useState<Array<T & { id: string }>>([]);
  useEffect(
    () =>
      onSnapshot(
        query(collection(db, col), where(field, "==", value)),
        (s) => setRows(s.docs.map((d) => ({ id: d.id, ...(d.data() as T) }))),
        () => setRows([]), // a section this person can't see just stays out
      ),
    [tenantId, col, field, value],
  );
  return rows;
}

export function CustomerHistory({
  tenantId,
  customerId,
  jobs,
  estimates,
  invoices,
}: {
  tenantId: string;
  customerId: string;
  jobs: Array<Job & { id: string }>;
  estimates: Array<Estimate & { id: string }>;
  invoices: Array<Invoice & { id: string }>;
}) {
  const texts = useList<MessageLog>(tenantId, paths.tenantMessages(tenantId), "customerId", customerId);
  const sends = useList<DocSend>(tenantId, paths.tenantDocSends(tenantId), "customerId", customerId);
  const signed = useList<Agreement>(tenantId, paths.tenantAgreements(tenantId), "customerId", customerId);
  const portal = useList<PortalContact>(tenantId, paths.tenantPortalContacts(tenantId), "customerId", customerId);
  const [filter, setFilter] = useState<"all" | Kind>("all");
  const [open, setOpen] = useState(false);
  const [limit, setLimit] = useState(25);

  const events = useMemo(() => {
    const ev: Ev[] = [];
    for (const m of texts) {
      ev.push({
        at: m.createdAt,
        kind: "text",
        icon: MessageSquare,
        title: m.direction === "in" ? "Text from the customer" : m.status === "sent" ? `Text sent${m.actorName ? ` by ${m.actorName}` : ""}` : `Text not sent (${m.status})`,
        detail: m.status === "failed" || m.status === "skipped" ? `${m.body.slice(0, 120)} · ${m.reason}` : m.body.slice(0, 160),
        href: m.jobId ? `/jobs/${m.jobId}` : undefined,
        tone: m.status === "failed" ? "bad" : undefined,
      });
    }
    const salesDoc = (d: (Estimate | Invoice) & { id: string; emailDelivery?: Record<string, { status: string }> }, label: string, href: string) => {
      for (const e of d.emails ?? []) {
        const delivery = e.id ? d.emailDelivery?.[e.id]?.status : undefined;
        ev.push({
          at: e.at,
          kind: "email",
          icon: Mail,
          title: `${label} emailed${e.byName ? ` by ${e.byName}` : ""}`,
          detail: `To ${e.to.join(", ")}${delivery ? ` · ${DELIVERY_LABEL[delivery as keyof typeof DELIVERY_LABEL] ?? delivery}` : ""}`,
          href,
          tone: delivery === "bounced" || delivery === "complained" ? "bad" : undefined,
        });
      }
    };
    for (const e of estimates) {
      const label = `Estimate #${e.number}`;
      const href = "/estimates";
      ev.push({ at: e.createdAt, kind: "money", icon: FileText, title: `${label} made`, detail: money(e.totalCents), href });
      if (e.sentAt) ev.push({ at: e.sentAt, kind: "money", icon: FileText, title: `${label} sent`, detail: money(e.totalCents), href });
      if (e.signedAt) ev.push({ at: e.signedAt, kind: "money", icon: FileSignature, title: `${label} signed${e.signerName ? ` by ${e.signerName}` : ""}`, href, tone: "good" });
      if (e.decidedAt && (e.status === "accepted" || e.status === "declined") && !(e.signedAt && e.status === "accepted"))
        ev.push({ at: e.decidedAt, kind: "money", icon: FileText, title: `${label} ${e.status}`, href, tone: e.status === "accepted" ? "good" : "bad" });
      salesDoc(e, label, href);
    }
    for (const i of invoices) {
      if (i.status === "draft") continue;
      const label = `Invoice #${i.number}`;
      const href = "/invoices";
      if (i.sentAt) ev.push({ at: i.sentAt, kind: "money", icon: Receipt, title: `${label} sent`, detail: money(i.totalCents), href });
      if (i.signedAt) ev.push({ at: i.signedAt, kind: "money", icon: FileSignature, title: `${label} signed${i.signerName ? ` by ${i.signerName}` : ""}`, href });
      for (const p of i.payments ?? []) {
        ev.push({ at: p.at, kind: "money", icon: CreditCard, title: `Payment ${money(p.amountCents)} on ${label}`, detail: [p.method === "ach" ? "bank transfer" : p.method, p.deposit ? "deposit" : "", p.note].filter(Boolean).join(" · "), href, tone: "good" });
      }
      if (i.voidedAt) ev.push({ at: i.voidedAt, kind: "money", icon: Receipt, title: `${label} voided`, href, tone: "bad" });
      if (i.status === "written_off" && i.writeOff) ev.push({ at: i.writeOff.at, kind: "money", icon: Receipt, title: `${label}: ${money(i.writeOff.amountCents)} written off${i.writeOff.byName ? ` by ${i.writeOff.byName}` : ""}`, detail: i.writeOff.note, href });
      salesDoc(i, label, href);
    }
    for (const j of jobs) {
      ev.push({ at: j.createdAt, kind: "job", icon: Wrench, title: `Job booked: ${j.title}`, detail: j.startAt ? `For ${when(j.startAt)}` : "No date yet", href: `/jobs/${j.id}` });
      if (j.doneAt) ev.push({ at: j.doneAt, kind: "job", icon: Wrench, title: `Job done: ${j.title}`, href: `/jobs/${j.id}`, tone: "good" });
    }
    for (const s of sends) {
      ev.push({
        at: s.sentAt,
        kind: "doc",
        icon: Mail,
        title: s.status === "failed" ? "Documents didn't send" : `Documents sent${s.sentByName ? ` by ${s.sentByName}` : ""}`,
        detail: `${s.docs.map((d) => d.name).join(", ")} → ${s.to.join(", ")}${s.delivery ? ` · ${DELIVERY_LABEL[s.delivery.status] ?? s.delivery.status}` : ""}`,
        tone: s.status === "failed" || s.delivery?.status === "bounced" ? "bad" : undefined,
      });
    }
    for (const a of signed) {
      ev.push({ at: a.createdAt, kind: "doc", icon: FileSignature, title: `${a.templateName} prepared`, href: a.jobId ? `/jobs/${a.jobId}` : undefined });
      if (a.signedAt) ev.push({ at: a.signedAt, kind: "doc", icon: FileSignature, title: `${a.templateName} signed${a.signerName ? ` by ${a.signerName}` : ""}`, href: a.jobId ? `/jobs/${a.jobId}` : undefined, tone: "good" });
      if (a.sentAt) ev.push({ at: a.sentAt, kind: "doc", icon: Mail, title: `${a.templateName} sent`, detail: a.sentHow });
    }
    for (const c of portal) {
      if (c.invitedAt) ev.push({ at: c.invitedAt, kind: "doc", icon: Mail, title: `Portal sign-in link sent to ${c.name || c.email}` });
      if (c.lastSignInAt) ev.push({ at: c.lastSignInAt, kind: "doc", icon: Mail, title: `${c.name || c.email || c.phone} used the customer portal`, detail: "Most recent visit" });
    }
    return ev.filter((e) => Number.isFinite(e.at) && e.at > 0).sort((a, b) => b.at - a.at);
  }, [texts, sends, signed, portal, jobs, estimates, invoices]);

  const shown = filter === "all" ? events : events.filter((e) => e.kind === filter);
  const paid = invoices.filter((i) => i.status !== "void").reduce((s, i) => s + i.amountPaidCents, 0);
  const owed = invoices.filter((i) => i.status === "sent" || i.status === "partially_paid").reduce((s, i) => s + i.totalCents - i.amountPaidCents, 0);

  return (
    <Card className="p-0">
      <div className={cn("flex flex-wrap items-center justify-between gap-2 px-4", open ? "pt-3" : "py-3")}>
        <button type="button" className="flex items-center gap-1.5 text-left" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
          {open ? <ChevronDown size={16} className="text-steel" /> : <ChevronRight size={16} className="text-steel" />}
          <CardTitle className="mb-0">History</CardTitle>
          <span className="text-xs text-steel">
            ({events.length}
            {events[0] ? `, latest ${when(events[0].at)}` : ""})
          </span>
        </button>
        {open ? (
        <Select className="w-auto py-1 text-xs" value={filter} aria-label="Show" onChange={(e) => setFilter(e.target.value as typeof filter)}>
          {FILTERS.map((f) => (
            <option key={f.id} value={f.id}>
              {f.label}
            </option>
          ))}
        </Select>
        ) : null}
      </div>
      {open ? (
      <>
      {paid || owed ? (
        <p className="px-4 pt-1 text-xs text-steel">
          Paid so far <strong className="text-ink">{money(paid)}</strong>
          {owed ? (
            <>
              {" "}
              · still owed <strong className="text-ink">{money(owed)}</strong>
            </>
          ) : null}
        </p>
      ) : null}
      <div className="mt-2">
        {shown.slice(0, limit).map((e, i) => {
          const Icon = e.icon;
          const body = (
            <>
              <Icon size={14} className={cn("mt-0.5 shrink-0", e.tone === "good" ? "text-success" : e.tone === "bad" ? "text-danger" : "text-steel")} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-ink">{e.title}</span>
                {e.detail ? <span className="block truncate text-xs text-steel">{e.detail}</span> : null}
              </span>
              <span className="shrink-0 text-xs text-steel">{when(e.at)}</span>
            </>
          );
          const cls = "flex items-start gap-2 border-b border-steel/10 px-4 py-1.5 text-sm last:border-0";
          return e.href ? (
            <Link key={i} href={e.href} className={cn(cls, "hover:bg-cream/60")}>
              {body}
            </Link>
          ) : (
            <div key={i} className={cls}>
              {body}
            </div>
          );
        })}
        {shown.length > limit ? (
          <button className="block w-full px-4 py-2 text-center text-xs text-steel hover:text-ink" onClick={() => setLimit((n) => n + 50)}>
            Show more ({shown.length - limit} older)
          </button>
        ) : null}
        {shown.length === 0 ? <p className="px-4 pb-3 text-xs text-steel">Nothing yet.</p> : null}
      </div>
      </>
      ) : null}
    </Card>
  );
}
